import { useCallback, useEffect } from 'react';

import { useDispatch, useSelector } from 'react-redux';

import { Commit } from 'src/components/types';
import { createCommitStatusMapper } from 'src/redux/commit-list/utils';
import { commit as commitSchema } from 'src/redux/pull-request/schemas';
import {
  branchCommitActions,
  BRANCH_UPDATE_COMMITS_STATUS,
} from 'src/redux/repo-commits/actions';
import { getCurrentRepositoryFullSlug } from 'src/selectors/repository-selectors';
import urls from 'src/urls/commit';
import authRequest, { jsonHeaders } from 'src/utils/fetch';

async function fetchBuildStatuses(
  repositoryFullSlug: string,
  commits: Commit[]
) {
  const url = urls.api.internal.commitsBuildStatuses(repositoryFullSlug);

  const request = authRequest(url, {
    method: 'POST',
    body: commits.map(commit => `commits=${commit.hash}`).join('&'),
    headers: {
      'content-type': 'application/x-www-form-urlencoded; charset=UTF-8',
    },
  });

  const response = await fetch(request);
  if (!response.ok) {
    return {};
  }

  return response.json();
}

async function fetchVerificationStatuses(
  repositoryFullSlug: string,
  commits: Commit[]
) {
  const url = urls.api.internal.verificationStatuses(repositoryFullSlug);
  const hashes = commits.map(commit => commit.hash);
  const body = JSON.stringify({ hashes });

  const request = authRequest(url, {
    method: 'POST',
    body,
    headers: jsonHeaders,
  });

  const response = await fetch(request);
  if (!response.ok) {
    return [];
  }

  return response.json();
}

export const useFetchCommitActions = () => {
  const dispatch = useDispatch();
  const fullName = useSelector(getCurrentRepositoryFullSlug);

  const fetchCommits = useCallback(
    async (commitUrl: string) => {
      try {
        const commitResponse = await fetch(authRequest(commitUrl));
        if (!commitResponse.ok) {
          const error = new Error('An error occured fetching the commits');
          // @ts-ignore
          error.status = commitResponse.status;
          throw error.message;
        }
        const { next, page, values: commits } = await commitResponse.json();
        dispatch({
          type: branchCommitActions.FETCH_COMMITS,
          meta: {
            schema: { commits: [commitSchema] },
          },
          payload: { next, page, commits },
        });
        return commits;
      } catch {
        dispatch({
          type: branchCommitActions.COMMIT_FETCH_ERROR,
        });
        return [];
      }
    },
    [dispatch]
  );

  const fetchStatuses = useCallback(
    async (commitList: Commit[]) => {
      if (!commitList?.length || !fullName) {
        return;
      }
      try {
        const buildStatuses = await fetchBuildStatuses(fullName, commitList);
        const verificationStatuses = await fetchVerificationStatuses(
          fullName,
          commitList
        );

        const addStatus = createCommitStatusMapper(
          buildStatuses,
          verificationStatuses
        );

        dispatch({
          type: BRANCH_UPDATE_COMMITS_STATUS.SUCCESS,
          payload: commitList.map(addStatus),
          meta: {
            schema: [commitSchema],
          },
        });
      } catch {
        dispatch({
          type: BRANCH_UPDATE_COMMITS_STATUS.ERROR,
        });
      }
    },
    [dispatch, fullName]
  );

  const fetchMoreBranchCommits = (nextUrl: string) => {
    dispatch({
      type: branchCommitActions.FETCH_MORE_COMMITS,
    });
    fetchCommits(nextUrl).then(commitList => fetchStatuses(commitList));
  };

  const retryFetchBranchCommits = (url: string) => {
    dispatch({
      type: branchCommitActions.RETRY_COMMITS,
    });
    fetchCommits(url).then(commitList => fetchStatuses(commitList));
  };

  return {
    fetchCommits,
    fetchStatuses,
    fetchMoreBranchCommits,
    retryFetchBranchCommits,
  };
};

export function useFetchBranchCommits(
  sourceHash: string,
  destHash: string,
  destRepoFullSlug: string
): void {
  const { fetchCommits, fetchStatuses } = useFetchCommitActions();
  const repositoryFullSlug = useSelector(getCurrentRepositoryFullSlug) || '';

  useEffect(() => {
    if (!repositoryFullSlug || !sourceHash || !destRepoFullSlug || !destHash) {
      return;
    }
    const commitsUrl = urls.api.internal.specCommitsApiUrl(
      repositoryFullSlug,
      sourceHash,
      destRepoFullSlug,
      destHash
    );
    fetchCommits(commitsUrl).then(commitList => fetchStatuses(commitList));
    // The url is missing because during an update to the dest repo and dest hash
    // the dest repo can update before the dest hash, which results in a call with
    // old dest hash and new dest hash
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchCommits, fetchStatuses, sourceHash, destHash]);
}
